<template>
  <div class="qr-manage-container">
    <!-- 페이지 제목 섹션 -->
    <div class="container-wrapper">
      <div class="page-title-container">
        <div class="page-title">
          <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">QR코드 관리</h3>
          <span class="pageDesc">동적 QR코드를 생성하고 목적지 URL을 관리할 수 있습니다.</span>
        </div>
      </div>
    </div>

    <!-- 검색 필터 섹션 -->
    <div class="container-wrapper">
      <qr-search-filter
        :initial-filters="filterData"
        :categories="categories"
        :tags="tags"
        @search="handleSearch"
        @reset="handleFilterReset"
        @filter-change="handleFilterChange"
      />
    </div>

    <!-- 목록 정보 및 QR코드 등록 버튼 -->
    <div class="container-wrapper">
      <div class="col-xs-12 table-info-filter">
        <div class="total-div">
          <p>전체 <span>{{ addCommaToNumber(listSize) }}</span>건</p>
        </div>
        <div class="search-container">
          <div class="select-wrapper">
            <select class="search-select" v-model="rowsPerPage" @change="handleRowsPerPageChange">
              <option :value="10">10개씩 보기</option>
              <option :value="20">20개씩 보기</option>
              <option :value="50">50개씩 보기</option>
            </select>
          </div>
          <a-dropdown :trigger="['click']">
            <a-button>
              설정 <down-outlined />
            </a-button>
            <template #overlay>
              <a-menu>
                <a-menu-item key="category" @click="openCategoryManageModal">
                  카테고리 관리
                </a-menu-item>
                <a-menu-item key="tag" @click="openTagManageModal">
                  태그 관리
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
          <button class="outline-button add-qr-btn" type="button" @click="openBulkUploadModal">
            <i class="fa-solid fa-upload"></i>
            <span>대량 등록</span>
          </button>
          <button class="yellow-button add-qr-btn" type="button" @click="openCreateModal">
            <i class="fa-solid fa-plus"></i>
            <span>QR코드 등록</span>
          </button>
        </div>
      </div>

      <!-- QR코드 리스트 테이블 -->
      <qr-table
        :items="qrList"
        :is-loading="isLoading"
        :base-url="baseUrl"
        :tags="tags"
        @edit="openEditModal"
        @delete="confirmDelete"
        @status-change="handleStatusChange"
        @url-change="openUrlChangeModal"
        @download="handleDownload"
        @preview="openPreviewModal"
      />

      <!-- 페이지네이션 -->
      <pagination
        :page-info="paginationConfig"
        page-name="QrManage"
        @change-page="paginationHandler"
      />
    </div>

    <!-- QR코드 등록/수정 모달 -->
    <qr-form-modal
      v-model:visible="formModalVisible"
      :edit-data="selectedQr"
      :categories="categories"
      :tags="tags"
      :base-url="baseUrl"
      @submit="handleFormSubmit"
    />

    <!-- URL 변경 모달 -->
    <qr-url-change-modal
      v-model:visible="urlChangeModalVisible"
      :qr-data="selectedQr"
      @success="handleUrlChangeSuccess"
    />

    <!-- 미리보기 모달 -->
    <qr-preview-modal
      v-model:visible="previewModalVisible"
      :qr-data="selectedQr"
      :base-url="baseUrl"
    />

    <!-- 카테고리 관리 모달 -->
    <category-manage-modal
      v-model:visible="categoryModalVisible"
      :categories="categories"
      @refresh="loadCategories"
    />

    <!-- 태그 관리 모달 -->
    <tag-manage-modal
      v-model:visible="tagModalVisible"
      :tags="tags"
      @refresh="loadTags"
    />

    <!-- 대량 등록 모달 -->
    <qr-bulk-upload-modal
      v-model:visible="bulkUploadModalVisible"
      :categories="categories"
      :tags="tags"
      @success="handleBulkUploadSuccess"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { DownOutlined } from '@ant-design/icons-vue';
import { addCommaToNumber, twoBtnDialog } from '@/utils/utils';
import { message } from 'ant-design-vue';
import Pagination from '@/views/components/Pagination.vue';
import QrSearchFilter from './components/QrSearchFilter.vue';
import QrTable from './components/QrTable.vue';
import QrFormModal from './components/QrFormModal.vue';
import QrUrlChangeModal from './components/QrUrlChangeModal.vue';
import QrPreviewModal from './components/QrPreviewModal.vue';
import CategoryManageModal from './components/CategoryManageModal.vue';
import TagManageModal from './components/TagManageModal.vue';
import QrBulkUploadModal from './components/QrBulkUploadModal.vue';
import QrService from './service/QrService';
import { resolveQrContentUrl } from './utils/qrUrls';
import QRCode from 'qrcode';

export default defineComponent({
  name: 'QrManage',
  components: {
    DownOutlined,
    Pagination,
    QrSearchFilter,
    QrTable,
    QrFormModal,
    QrUrlChangeModal,
    QrPreviewModal,
    CategoryManageModal,
    TagManageModal,
    QrBulkUploadModal
  },

  setup() {
    // 상태
    const qrList = ref([]);
    const categories = ref([]);
    const tags = ref([]);
    const isLoading = ref(false);
    const listSize = ref(0);
    const rowsPerPage = ref(10);
    const currentPage = ref(1);
    const totalPages = ref(0);

    const filterData = ref({
      categoryId: undefined,
      status: undefined,
      tagId: undefined,
      keyword: ''
    });

    // 모달 상태
    const formModalVisible = ref(false);
    const urlChangeModalVisible = ref(false);
    const previewModalVisible = ref(false);
    const categoryModalVisible = ref(false);
    const tagModalVisible = ref(false);
    const bulkUploadModalVisible = ref(false);
    const selectedQr = ref(null);

    // 환경 설정 - QR 리다이렉트는 외부 공개용 Open API 서버 사용
    const baseUrl = computed(() => {
      return process.env.VUE_APP_OPEN_SERVERURL || window.location.origin;
    });

    // API 파라미터
    const apiParams = computed(() => {
      const params = {
        page: currentPage.value,
        limit: rowsPerPage.value
      };

      if (filterData.value.categoryId) {
        params.categoryId = filterData.value.categoryId;
      }
      if (filterData.value.status) {
        params.status = filterData.value.status;
      }
      if (filterData.value.tagId) {
        params.tagId = filterData.value.tagId;
      }
      if (filterData.value.keyword) {
        params.keyword = filterData.value.keyword;
      }

      return params;
    });

    // 페이지네이션 설정
    const paginationConfig = computed(() => ({
      currentPage: currentPage.value,
      totalPages: totalPages.value
    }));

    // 데이터 로드
    const loadQrList = async () => {
      if (isLoading.value) return;

      try {
        isLoading.value = true;
        const result = await QrService.getQrList(apiParams.value);
        qrList.value = result.items;
        listSize.value = result.total;
        totalPages.value = result.totalPages;
      } finally {
        isLoading.value = false;
      }
    };

    const loadCategories = async () => {
      categories.value = await QrService.getCategories();
    };

    const loadTags = async () => {
      tags.value = await QrService.getTags();
    };

    // 이벤트 핸들러
    const handleFilterChange = (filters) => {
      filterData.value = { ...filters };
    };

    const handleSearch = (filters) => {
      filterData.value = { ...filters };
      currentPage.value = 1;
      loadQrList();
    };

    const handleFilterReset = (filters) => {
      filterData.value = { ...filters };
      currentPage.value = 1;
      loadQrList();
    };

    const handleRowsPerPageChange = () => {
      currentPage.value = 1;
      loadQrList();
    };

    const paginationHandler = (childPassParam) => {
      currentPage.value = childPassParam.page;
      loadQrList();
    };

    // QR코드 CRUD
    const openCreateModal = () => {
      selectedQr.value = null;
      formModalVisible.value = true;
    };

    const openEditModal = (qr) => {
      selectedQr.value = qr;
      formModalVisible.value = true;
    };

    const handleFormSubmit = async (formData, isEdit) => {
      let success;

      if (isEdit && selectedQr.value) {
        success = await QrService.updateQr(selectedQr.value._id, formData);
      } else {
        success = await QrService.createQr(formData);
      }

      if (success) {
        formModalVisible.value = false;
        loadQrList();
      }
    };

    const confirmDelete = async (qr) => {
      const isConfirmed = await twoBtnDialog(
        'QR코드 삭제',
        `"${qr.name}" QR코드를 삭제하시겠습니까?\n삭제된 QR코드는 복구할 수 없습니다.`,
        '삭제'
      );

      if (!isConfirmed) return;

      const success = await QrService.deleteQr(qr._id);
      if (success) {
        loadQrList();
      }
    };

    const handleStatusChange = async (qr, newStatus) => {
      const success = await QrService.updateStatus(qr._id, newStatus);
      if (success) {
        loadQrList();
      }
    };

    // URL 변경
    const openUrlChangeModal = (qr) => {
      selectedQr.value = qr;
      urlChangeModalVisible.value = true;
    };

    const handleUrlChangeSuccess = () => {
      loadQrList();
    };

    // 미리보기
    const openPreviewModal = (qr) => {
      selectedQr.value = qr;
      previewModalVisible.value = true;
    };

    // 다운로드
    const handleDownload = async (qr) => {
      const qrUrl = resolveQrContentUrl(baseUrl.value, qr);
      if (!qrUrl) {
        message.warning('QR코드 URL을 확인할 수 없습니다.');
        return;
      }
      const filename = `qr-${qr.shortCode}`;

      try {
        const canvas = document.createElement('canvas');
        await QRCode.toCanvas(canvas, qrUrl, {
          width: 300,
          margin: 2
        });

        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        message.success('QR코드가 다운로드되었습니다.');
      } catch (err) {
        console.error('QR 다운로드 오류:', err);
        message.error('다운로드에 실패했습니다.');
      }
    };

    // 카테고리/태그 관리
    const openCategoryManageModal = () => {
      categoryModalVisible.value = true;
    };

    const openTagManageModal = () => {
      tagModalVisible.value = true;
    };

    // 대량 등록
    const openBulkUploadModal = () => {
      bulkUploadModalVisible.value = true;
    };

    const handleBulkUploadSuccess = () => {
      loadQrList();
      loadCategories();
      loadTags();
    };

    // 초기화
    onMounted(() => {
      loadCategories();
      loadTags();
      loadQrList();
    });

    return {
      // 상태
      qrList,
      categories,
      tags,
      isLoading,
      listSize,
      rowsPerPage,
      currentPage,
      totalPages,
      filterData,
      baseUrl,

      // 모달 상태
      formModalVisible,
      urlChangeModalVisible,
      previewModalVisible,
      categoryModalVisible,
      tagModalVisible,
      bulkUploadModalVisible,
      selectedQr,

      // Computed
      paginationConfig,

      // Methods
      addCommaToNumber,
      handleFilterChange,
      handleSearch,
      handleFilterReset,
      handleRowsPerPageChange,
      paginationHandler,
      openCreateModal,
      openEditModal,
      handleFormSubmit,
      confirmDelete,
      handleStatusChange,
      openUrlChangeModal,
      handleUrlChangeSuccess,
      openPreviewModal,
      handleDownload,
      openCategoryManageModal,
      openTagManageModal,
      openBulkUploadModal,
      handleBulkUploadSuccess,
      loadCategories,
      loadTags
    };
  }
});
</script>

<style lang="scss" scoped>
@import './styles/common.scss';

.qr-manage-container {
  padding: 1vw;

  .search-container {
    display: flex;
    align-items: center;
    gap: $spacing-md;

    .select-wrapper {
      min-width: 130px;
    }

    .search-select {
      width: 100%;
      height: 38px;
      border: 1px solid $border-color-dark;
      border-radius: $border-radius-sm;
      padding: 0 $spacing-sm;
      background-color: $bg-white;
      cursor: pointer;

      &:focus {
        outline: none;
        border-color: $yellow-button-color;
      }
    }

    .add-qr-btn {
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 $spacing-md;

      i {
        margin-right: $spacing-xs;
      }
    }

    .outline-button {
      background-color: transparent;
      border: 1px solid $border-color-dark;
      border-radius: $border-radius-sm;
      color: $text-dark;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: $yellow-button-color;
        color: $yellow-button-color;
      }
    }
  }
}
</style>
